"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef, useState } from "react";

import CompanyLogo from "@/components/company-logo";
import { Route } from "@/enums/route";
import { noteSilentSignal } from "@/lib/noteSilentSignal";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import {
  isSplashAutoRecoverExhausted,
  recordSplashAutoRecoverAttempt,
} from "@/lib/splashAutoRecover";
import {
  clearSigninRedirectAttempts,
  isSigninRedirectExhausted,
  isSigninRedirectInFlight,
} from "@/providers/AuthProvider/signinRedirectAttempts";
import { OIDC_REQUEST_TIMEOUT_SECONDS } from "@/providers/AuthProvider/userManagerSingleton";

import { watchFirstUserInteraction } from "./firstUserInteraction";
import { resolveSplashAction } from "./resolveSplashAction";
import { resolveStuckReport } from "./resolveStuckReport";
import {
  armSplashRecoveryWatcher,
  type SplashRecoveryWatcher,
} from "./splashRecoveryWatcher";

// If the splash is still visible at this point AND the URL carries a failed
// OAuth callback (?error), attempt ONE automatic clean reload to drop the stale
// params before bothering the user. Narrow by design — see resolveSplashAction.
const AUTO_RECOVER_AFTER_MS = 5000;
// If the splash is STILL up this long after first paint, surface the manual
// "Reload app" escape hatch. This is the team-calibrated "likely wedged" point
// and the catch-all for every stuck splash that auto-recovery did not handle.
const SHOW_RELOAD_AFTER_MS = 8000;
// The "Reload app" button is a harmless UX affordance, but the error-level
// `boot-splash-stuck` event must fire only for a GENUINE wedge. A wedge keeps
// the splash up forever; a slow-but-healthy boot (cold OAuth exchange + the four
// boot fetches on a poor link) clears within a few more seconds. So after the
// button appears, wait this long and RE-CHECK before alerting the team — a boot
// that merely ran long is not a failure. ~7s ⇒ ~15s total. (CUSTOMER-UI-3Z: the
// 8s-only check fired on slow boots and backgrounded tabs that rendered fine.)
const STUCK_CONFIRM_AFTER_MS = 7000;
// How long to let the auto-recovery telemetry event flush before navigating
// away — the batch beacon may not flush on unload, so we wait briefly to keep
// the recovery observable. Resolves immediately when Sentry is disabled.
const RECOVER_FLUSH_TIMEOUT_MS = 2000;
// Once the ~15s checkpoint says "stuck", watch the veil this long BEFORE
// committing the error. A clear inside the window means the boot was merely
// slow (the CUSTOMER-UI-3Z Mode B class) and records an observable-only
// boot-splash-recovered signal instead; only a veil that outlives the window
// is reported as boot-splash-stuck. ~30s ⇒ the error fires at ~45s total.
// That delay is affordable precisely because a genuine wedge never clears,
// and it is what makes the error mean "the app really failed to render".
// See splashRecoveryWatcher.ts.
const RECOVERY_WATCH_WINDOW_MS = 30_000;
// If the confirmation checkpoint lands while an OAuth code→token exchange is
// STILL in flight, wait this long before re-checking. Derived from the OIDC
// request timeout so the two can never drift: the exchange is already bounded
// at OIDC_REQUEST_TIMEOUT_SECONDS, after which oidc-client-ts rejects and the
// existing recovery path (resolveAuthAction → re-redirect → budget → parked
// Reload state) takes over. Reporting at the ~15s checkpoint alarms INSIDE that
// bound, which is what produced the CUSTOMER-UI-3Z `boot.hasAuthCallbackParams:
// true` false positives. The deferral is one-shot — a hung exchange still
// reports, just after auth has actually had its full budget.
const AUTH_SETTLE_RECHECK_MS = OIDC_REQUEST_TIMEOUT_SECONDS * 1000;

// OAuth error codes are a fixed server-side vocabulary (temporarily_unavailable,
// access_denied, ...). Anything not matching this shape is replaced rather than
// sent — extraction over subtraction, per the Sentry PII rules.
const OAUTH_CODE_PATTERN = /^[a-z0-9_-]{1,64}$/i;
const sanitizeOAuthCode = (value: string | null): string =>
  value === null ? "none" : OAUTH_CODE_PATTERN.test(value) ? value : "other";

export interface SplashScreenLoaderProps {
  /** Override the auto-recover delay (Storybook/tests only). */
  autoRecoverAfterMs?: number;
  /** Override the reload-button delay (Storybook/tests only). */
  reloadAfterMs?: number;
  /** Override the stuck-confirmation re-check delay (Storybook/tests only). */
  stuckConfirmAfterMs?: number;
  /** Override the pre-report recovery watch window (Storybook/tests only). */
  recoveryWatchWindowMs?: number;
  /**
   * Whether the automatic one-shot recovery (a clean page reload) may fire.
   * Defaults to `true` in production; Storybook/tests pass `false` so rendering
   * the splash never navigates the page away.
   */
  enableAutoRecover?: boolean;
}

const SplashScreenLoader = ({
  autoRecoverAfterMs = AUTO_RECOVER_AFTER_MS,
  reloadAfterMs = SHOW_RELOAD_AFTER_MS,
  stuckConfirmAfterMs = STUCK_CONFIRM_AFTER_MS,
  recoveryWatchWindowMs = RECOVERY_WATCH_WINDOW_MS,
  enableAutoRecover = true,
}: SplashScreenLoaderProps) => {
  const [showReload, setShowReload] = useState(false);
  // At most one user-facing surface (button + alert) per mount. A ref (not a
  // closure-local) so it survives an effect re-run from a prop change
  // (Storybook/tests) without re-surfacing.
  const surfacedRef = useRef(false);

  useEffect(() => {
    // Did the user tap/press a key while the splash was up? Reported as the
    // boot.userInteracted tag — a tag only, never a reason to skip a report.
    const interaction = watchFirstUserInteraction(window);

    // removeLoader() hides the splash via inline opacity/display — treat it as
    // "still stuck" only while it is actually visible.
    const isSplashVisible = (): boolean => {
      const loader = document.getElementById("globalLoader");
      return Boolean(
        loader &&
        loader.style.opacity !== "0" &&
        loader.style.display !== "none"
      );
    };

    // Drop the stale ?error/?state params by hard-navigating to the clean root.
    // Deliberately does NOT clear the signin-redirect budget: that is
    // AuthController's cross-page-load loop guard (CUSTOMER-UI-3T), and resetting
    // it automatically would let a broken-callback loop exceed its attempt cap.
    // The one-shot budget (lib/splashAutoRecover, cap 1/60s) prevents this from
    // looping; AuthController retries auth within its own remaining budget.
    const performAutoRecover = (oauthError: string): void => {
      // Spend the budget FIRST and unconditionally — loop-safety is paramount.
      // A recovery that briefly fails to ship telemetry is recoverable; a
      // machine-speed reload loop is not. So the one-shot cap must be recorded
      // before anything below can throw.
      recordSplashAutoRecoverAttempt();

      // Navigation is GUARANTEED and idempotent. Telemetry (report or flush)
      // must never be able to block the recovery it reports — if reportSilent
      // Failure throws, or Sentry.flush() throws / rejects / never settles, we
      // still navigate. The absolute-deadline timer is the backstop for a flush
      // promise that hangs forever (which would otherwise strand the user here).
      let navigated = false;
      const navigateOnce = (): void => {
        if (navigated) return;
        navigated = true;
        window.location.assign(Route.Dashboard);
      };

      // A warning (NOT an error): the failed-callback wedge is self-healing, but
      // the team needs the trend — silencing it would re-open the June 2026
      // frozen-splash blind spot. The persistent case still escalates to the
      // boot-splash-stuck error on the reloaded page.
      try {
        reportSilentFailure({
          message: "[Boot] Splash auto-recovering after failed auth callback",
          level: "warning",
          fingerprint: "boot-splash-auto-recover",
          tags: {
            "boot.stuck": "true",
            "boot.autoRecover": "true",
            "boot.oauthError": oauthError,
          },
          extra: { stuckAfterMs: autoRecoverAfterMs },
        });
      } catch {
        // Never let a telemetry throw strand the user on the splash.
      }

      // Flush before navigating so the self-heal stays observable (the batch
      // beacon may not flush on unload). Capped at RECOVER_FLUSH_TIMEOUT_MS:
      // beyond that we navigate unseen rather than wait — a network degraded
      // enough to exceed it has likely already lost the recovery to other
      // systems too. The +250ms backstop covers a flush promise that never
      // settles.
      try {
        void Sentry.flush(RECOVER_FLUSH_TIMEOUT_MS).then(
          navigateOnce,
          navigateOnce
        );
      } catch {
        navigateOnce();
      }
      setTimeout(navigateOnce, RECOVER_FLUSH_TIMEOUT_MS + 250);
    };

    // The confirmation re-check timer + the one-shot foreground listener it may
    // arm. Declared here so the effect cleanup can clear both.
    let confirmTimer: ReturnType<typeof setTimeout> | undefined;
    let onVisibilityChange: (() => void) | undefined;
    // The post-report recovery watcher (armed only after the stuck error has
    // fired) — held so the effect cleanup can disarm it.
    let recoveryWatcher: SplashRecoveryWatcher | undefined;
    // The auth-settle re-check timer, plus the latch that makes that deferral
    // ONE-SHOT. Without the latch a callback whose params never clear would
    // defer forever and silently swallow a real wedge; with it, the second pass
    // reports regardless of callback state.
    let authSettleTimer: ReturnType<typeof setTimeout> | undefined;
    let authSettleDeferred = false;

    // True while the app is still processing an INBOUND Keycloak callback: the
    // URL carries ?code+?state and no ?error. An errored callback is excluded —
    // that is the auto-recover path (checkpoint 1), not a healthy pending
    // exchange.
    const isAuthCallbackInFlight = (): boolean => {
      const params = new URLSearchParams(window.location.search);
      return Boolean(
        params.get("code") && params.get("state") && !params.get("error")
      );
    };

    // Decide whether this splash is a candidate wedge, then hand the VERDICT to
    // the recovery watcher — nothing is reported from here. A backgrounded tab
    // throttled the timers, so its elapsed time is meaningless: defer until the
    // tab is foreground and re-evaluate. See resolveStuckReport.
    const evaluateStuckSplash = (): void => {
      const decision = resolveStuckReport({
        splashVisible: isSplashVisible(),
        redirectInFlight: isSigninRedirectInFlight(),
        tabVisible: document.visibilityState === "visible",
        // Latched: the re-check pass always passes false, so the deferral can
        // fire at most once and a hung exchange is delayed, never dropped.
        authCallbackInFlight: !authSettleDeferred && isAuthCallbackInFlight(),
      });
      if (decision === "skip") return;
      if (decision === "defer-until-visible") {
        if (onVisibilityChange) return;
        const handler = (): void => {
          if (document.visibilityState !== "visible") return;
          document.removeEventListener("visibilitychange", handler);
          onVisibilityChange = undefined;
          evaluateStuckSplash();
        };
        onVisibilityChange = handler;
        document.addEventListener("visibilitychange", handler);
        return;
      }
      if (decision === "defer-until-auth-settles") {
        authSettleDeferred = true;
        authSettleTimer = setTimeout(
          evaluateStuckSplash,
          AUTH_SETTLE_RECHECK_MS
        );
        return;
      }
      // Read at EMISSION time, not at checkpoint time: the recovery window is
      // ~30s long and the auto-recover / redirect budgets can be spent inside
      // it, so a snapshot taken at the checkpoint would misreport the state the
      // event actually describes.
      const snapshotBootTags = (): Record<string, string> => {
        const params = new URLSearchParams(window.location.search);
        return {
          "boot.stuck": "true",
          "boot.signinRedirectInFlight": "false",
          // True ⇒ an automatic recovery already ran this window and the wedge
          // persisted — a genuinely unrecoverable boot (the key signal here).
          "boot.autoRecoverAttempted": String(isSplashAutoRecoverExhausted()),
          // Budget spent (the parked-loop signal) is also reported error-level
          // by AuthController as auth-redirect-budget-exhausted.
          "boot.signinRedirectExhausted": String(isSigninRedirectExhausted()),
          "boot.hasAuthCallbackParams": String(
            Boolean(
              (params.get("code") || params.get("error")) && params.get("state")
            )
          ),
          "boot.oauthError": sanitizeOAuthCode(params.get("error")),
          // True ⇒ someone was actively waiting on this splash (CUSTOMER-UI-3Z
          // triage: a user stuck in front of it vs an unattended tab).
          "boot.userInteracted": String(interaction.hasInteracted()),
        };
      };

      // Do NOT commit the error yet. At this checkpoint the splash is stuck
      // *so far*, which is not the same as failed — a cold OAuth exchange plus
      // the four boot fetches on a poor link can still land after it. Hand the
      // verdict to the watcher: it reports the error ONLY if the veil is still
      // up a full window later, by which point a healthy boot has rendered.
      // That is what makes boot-splash-stuck mean "the app really crashed"
      // rather than "the app was slow" (the CUSTOMER-UI-3Z false positives).
      // surfacedRef guarantees at most one watcher per mount.
      recoveryWatcher = armSplashRecoveryWatcher({
        loader: document.getElementById("globalLoader"),
        isSplashVisible,
        windowMs: recoveryWatchWindowMs,
        onRecovered: (recoveredAfterMs) => {
          try {
            noteSilentSignal({
              message: "[Boot] Splash recovered after stuck report",
              signal: "boot-splash-recovered",
              tags: snapshotBootTags(),
              // ms since the checkpoint, so total age is
              // reloadAfterMs + stuckConfirmAfterMs + this.
              extra: { recoveredAfterMs },
            });
          } catch {
            // Telemetry only — never let a report throw into the boot path.
          }
        },
        onStillStuck: () => {
          try {
            reportSilentFailure({
              message: "[Boot] Splash screen stuck — app failed to render",
              level: "error",
              fingerprint: "boot-splash-stuck",
              tags: snapshotBootTags(),
              extra: {
                // The CONFIRMED wedge age — the splash was still up this long
                // after mount (button deadline + confirmation + watch window).
                stuckAfterMs:
                  reloadAfterMs + stuckConfirmAfterMs + recoveryWatchWindowMs,
                oauthErrorDescription: sanitizeOAuthCode(
                  new URLSearchParams(window.location.search).get(
                    "error_description"
                  )
                ),
              },
            });
          } catch {
            // Telemetry only — never let a report throw into the boot path.
          }
        },
      });
    };

    // Surface the manual "Reload app" button at the 8s deadline — a harmless
    // escape hatch for a user genuinely waiting. Do NOT alert yet: a splash
    // still up with a Keycloak redirect in flight is the healthy slow round-trip
    // (CUSTOMER-UI-6B), and a slow-but-healthy boot is still about to render.
    // Schedule the confirmation re-check; evaluateStuckSplash then hands the
    // verdict to a further watch window, so the error fires only if the wedge
    // outlives both (eliminates the CUSTOMER-UI-3Z slow-boot false positives).
    const surfaceStuckSplash = (): void => {
      if (surfacedRef.current || !isSplashVisible()) return;
      surfacedRef.current = true;
      setShowReload(true);
      if (isSigninRedirectInFlight()) return;
      confirmTimer = setTimeout(evaluateStuckSplash, stuckConfirmAfterMs);
    };

    // Checkpoint 1: attempt the one-shot automatic recovery, but only for a
    // failed-callback wedge (a clean reload that demonstrably helps and can't be
    // a healthy slow boot). Every other state waits for checkpoint 2.
    const autoRecoverTimer = setTimeout(() => {
      const visible = isSplashVisible();
      const params = visible
        ? new URLSearchParams(window.location.search)
        : null;
      const action = resolveSplashAction({
        loaderVisible: visible,
        redirectInFlight: isSigninRedirectInFlight(),
        hasRecoverableError: params?.get("error") != null,
        autoRecoverEligible:
          enableAutoRecover && !isSplashAutoRecoverExhausted(),
      });
      if (action === "auto-recover" && params) {
        performAutoRecover(sanitizeOAuthCode(params.get("error")));
      }
    }, autoRecoverAfterMs);

    // Checkpoint 2: still stuck — surface the manual escape hatch. The catch-all
    // for every wedge auto-recovery did not (or could not) handle.
    const reloadButtonTimer = setTimeout(surfaceStuckSplash, reloadAfterMs);

    return () => {
      clearTimeout(autoRecoverTimer);
      clearTimeout(reloadButtonTimer);
      if (confirmTimer) clearTimeout(confirmTimer);
      if (authSettleTimer) clearTimeout(authSettleTimer);
      if (onVisibilityChange) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      recoveryWatcher?.disarm();
      interaction.dispose();
    };
  }, [
    autoRecoverAfterMs,
    reloadAfterMs,
    stuckConfirmAfterMs,
    recoveryWatchWindowMs,
    enableAutoRecover,
  ]);

  return (
    <div id="globalLoader">
      <CompanyLogo
        id="globalLoaderSpinner"
        width={400}
        height={300}
        variant="default"
      />
      {showReload && (
        // Full reload to the clean root URL drops any stale query params
        // (e.g. ?error=...&state=... from a failed auth callback). The explicit
        // user retry ALSO resets the automatic signin-redirect budget that may
        // have parked the app on this splash (a deliberate human action, unlike
        // the automatic recovery above, which leaves that budget intact).
        // Hardcoded English: the splash renders outside NextIntlClientProvider
        // (same exception as global-error.tsx / GlobalErrorListener).
        <button
          id="globalLoaderReload"
          type="button"
          onClick={() => {
            clearSigninRedirectAttempts();
            window.location.assign(Route.Dashboard);
          }}
        >
          <span>Reload app</span>
        </button>
      )}
    </div>
  );
};

export default SplashScreenLoader;
